import React, { useState } from "react";
import { Accordion, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBasketBuyerInfoModal from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketBuyerInfoModal";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickStartGuideAccordionRow from "@/features/pathpick/features/start-guide/components/PathPickStartGuideAccordionRow";
import PathPickStartGuideProductTypeNotice from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideProductTypeNotice";
import PathPickStartGuideOverseasPermissionModal from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideOverseasPermissionModal";
import PathPickStartGuideOverseasReleaseModal from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideOverseasReleaseModal";
import PathPickStartGuideApiSettingModal from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideApiSettingModal";
import PathPickStartGuideDeliveryTemplateModal from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideDeliveryTemplateModal";
import { READINESS_TASK_TITLE_KEYS } from "@/features/pathpick/features/start-guide/constants";
import { StartGuideReadiness } from "@/features/pathpick/features/start-guide/hooks/useStartGuideReadiness";
import {
  StartGuideReadinessTaskId,
  StartGuideSalesMode,
} from "@/features/pathpick/features/start-guide/types";

interface Props {
  readiness: StartGuideReadiness;
  // KREAM 판매 방식은 등록만이 아니라 주문까지 이 화면에서 준비한다 — 문구가 갈린다.
  salesMode: StartGuideSalesMode;
  onCompleteTask: (task: StartGuideReadinessTaskId) => void;
  onGoToProductTour: () => void;
}

// 모달로 여는 태스크 — 펼친 본문에 요약 전문과 이 버튼만 있으면 된다.
// (상품 유형 안내만 예외로 인라인 폼을 갖는다.)
const MODAL_TASKS = [
  "overseas_permission",
  "overseas_release",
  "api_setting",
  "delivery_template",
  "buyer_info",
] as const;
type ModalTaskId = (typeof MODAL_TASKS)[number];

const isModalTask = (task: StartGuideReadinessTaskId): task is ModalTaskId =>
  (MODAL_TASKS as readonly string[]).includes(task);

/**
 * 설정 요약 카드 + 설정 목록 아코디언.
 *
 * 어떤 태스크가 나오는지는 `useStartGuideReadiness` 가 정하고, 여기서는 그 목록을
 * 아코디언 행과 모달에 연결하기만 한다. API/엑셀 조건부 항목이 뒤바뀌지 않는 이유가 이 분리다.
 */
function PathPickStartGuideReadinessList({
  readiness,
  salesMode,
  onCompleteTask,
  onGoToProductTour,
}: Props) {
  const { t } = useI18nHelper();
  // 요약 카드 문구만 판매 방식으로 갈린다 — 목록 문구는 스마트스토어 전용이라 그대로다.
  const summaryPrefix =
    salesMode === "kream"
      ? "path_pick_guide:readiness.kream"
      : "path_pick_guide:readiness";
  // 펼쳐 둔 행 (한 번에 하나). 모달은 이와 별개로 열린다 — 행을 펼치기만 하고
  // 액션 버튼을 누르지 않은 상태가 있어야 하기 때문이다.
  const [expandedTask, setExpandedTask] =
    useState<StartGuideReadinessTaskId | null>(
      () => readiness.tasks.find((task) => !task.isCompleted)?.id ?? null
    );
  const [openModal, setOpenModal] = useState<ModalTaskId | null>(null);

  const expandedIndex = readiness.tasks.findIndex(
    (task) => task.id === expandedTask
  );

  // 완료 처리 후에는 아직 남은 태스크로 자연스럽게 넘어간다.
  const nextIncompleteAfter = (task: StartGuideReadinessTaskId) =>
    readiness.tasks.find((it) => it.id !== task && !it.isCompleted)?.id ?? null;

  const complete = (task: StartGuideReadinessTaskId) => {
    onCompleteTask(task);
    setOpenModal(null);
    setExpandedTask(nextIncompleteAfter(task));
  };

  return (
    <>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t(`${summaryPrefix}.title`)}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t(`${summaryPrefix}.description`)}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        {/* 진행 상황은 숫자로만 알린다 — 큰 진행률 블록은 쓰지 않는다(전달서 §4). */}
        <PathPickStartGuideStepPanel
          title={
            <Text
              as={"span"}
              color={readiness.isAllDone ? "#247A52" : "#1272EF"}
              role={"status"}
            >
              {readiness.totalCount === 0
                ? t(`${summaryPrefix}.all_done_progress`)
                : t("path_pick_guide:readiness.progress", {
                    total: readiness.totalCount,
                    done: readiness.doneCount,
                  })}
            </Text>
          }
          actions={
            // 남은 항목이 없을 때만 다음 단계 CTA가 생긴다(전달서 §8).
            // 할 일이 애초에 없는 KREAM 전용도 여기에 포함된다.
            readiness.isReadyForNextStep ? (
              <AppButton variant={"primary"} onClick={onGoToProductTour}>
                {t("path_pick_guide:nav.next_step")}
              </AppButton>
            ) : null
          }
        />
      </PathPickStartGuideCard>

      {/* 필요한 설정이 하나도 없으면(KREAM 전용) 빈 목록 카드를 만들지 않는다. */}
      {readiness.tasks.length > 0 && (
        <PathPickStartGuideCard
          hasHeaderDivider={true}
          bodyPadding={"0"}
          header={
            <Flex flexDirection={"column"} gap={"4px"}>
              <Text textStyle={"body-md-bold"} color={"#22221F"}>
                {t("path_pick_guide:readiness.section_title")}
              </Text>
              <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                {t("path_pick_guide:readiness.section_description")}
              </Text>
            </Flex>
          }
        >
          {/* allowToggle 을 켜지 않는다 — 항상 한 행이 열려 있고, 열린 행을 다시 눌러도 접히지 않는다. */}
          <Accordion
            as={"div"}
            role={"list"}
            aria-label={t("path_pick_guide:readiness.list_aria_label")}
            index={expandedIndex}
            onChange={(index) => {
              const next = typeof index === "number" ? index : index[0];
              if (next === undefined || next < 0) {
                return;
              }
              setExpandedTask(readiness.tasks[next]?.id ?? null);
            }}
          >
            {readiness.tasks.map((task) => {
              const isExpanded = task.id === expandedTask;
              return (
                <PathPickStartGuideAccordionRow
                  key={task.id}
                  title={t(READINESS_TASK_TITLE_KEYS[task.id])}
                  isExpanded={isExpanded}
                  badge={
                    <PathPickBadge
                      label={t(
                        task.isCompleted
                          ? "path_pick_guide:readiness.badge_done"
                          : "path_pick_guide:readiness.badge_todo"
                      )}
                      colorVariant={task.isCompleted ? "green" : "yellow"}
                    />
                  }
                  // 상품 유형 안내는 목록에서 제목만 보여준다(전달서 §8.1).
                  summary={
                    isModalTask(task.id)
                      ? t(`path_pick_guide:${task.id}.summary`)
                      : undefined
                  }
                >
                  {task.id === "product_type" ? (
                    <PathPickStartGuideProductTypeNotice
                      isCompleted={task.isCompleted}
                      onComplete={() => {
                        onCompleteTask("product_type");
                        // 전달서 §8.1 — 완료 처리하고 곧바로 해외 판매 권한 모달을 연다.
                        setExpandedTask("overseas_permission");
                        setOpenModal("overseas_permission");
                      }}
                    />
                  ) : (
                    <Flex flexDirection={"column"} gap={"12px"}>
                      <Text
                        textStyle={"body-sm-medium"}
                        color={"#6F6F6C"}
                        whiteSpace={"pre-line"}
                      >
                        {t(`path_pick_guide:${task.id}.summary`)}
                      </Text>
                      <Flex justifyContent={"flex-start"}>
                        <AppButton
                          variant={"primary"}
                          onClick={() =>
                            isModalTask(task.id) && setOpenModal(task.id)
                          }
                        >
                          {t(
                            task.id === "buyer_info" && task.isCompleted
                              ? "path_pick_guide:buyer_info.action_completed"
                              : `path_pick_guide:${task.id}.action`
                          )}
                        </AppButton>
                      </Flex>
                    </Flex>
                  )}
                </PathPickStartGuideAccordionRow>
              );
            })}
          </Accordion>
        </PathPickStartGuideCard>
      )}

      <PathPickStartGuideOverseasPermissionModal
        isOpen={openModal === "overseas_permission"}
        onClose={() => setOpenModal(null)}
        onComplete={() => complete("overseas_permission")}
      />
      <PathPickStartGuideOverseasReleaseModal
        isOpen={openModal === "overseas_release"}
        onClose={() => setOpenModal(null)}
        onComplete={() => complete("overseas_release")}
      />
      <PathPickStartGuideApiSettingModal
        isOpen={openModal === "api_setting"}
        onClose={() => setOpenModal(null)}
        isCompleted={
          readiness.tasks.find((task) => task.id === "api_setting")
            ?.isCompleted ?? false
        }
        onComplete={() => complete("api_setting")}
      />
      <PathPickStartGuideDeliveryTemplateModal
        isOpen={openModal === "delivery_template"}
        onClose={() => setOpenModal(null)}
        isCompleted={
          readiness.tasks.find((task) => task.id === "delivery_template")
            ?.isCompleted ?? false
        }
        onSave={() => {
          // 저장해도 모달은 닫지 않는다 — 저장된 코드를 그 자리에서 확인할 수 있어야 한다.
          onCompleteTask("delivery_template");
        }}
      />
      {/* 판매자 기본 정보는 실제 모달을 그대로 쓴다 — 가이드용 폼을 따로 만들지 않는다(전달서 §8.6).
          가이드 전용 variant 가 API/엑셀별 필드 노출(템플릿 코드 섹션)을 갈라 주면서,
          두 흐름 모두 신청 관문용 배너와 필수 검사는 빼 준다. */}
      {openModal === "buyer_info" && (
        <PathPickBasketBuyerInfoModal
          isOpen={true}
          onClose={() => setOpenModal(null)}
          variant={salesMode === "api" ? "guideApi" : "guideExcel"}
          isBlocked={false}
          isResumingRequest={false}
          onSaved={() => complete("buyer_info")}
        />
      )}
    </>
  );
}

export default PathPickStartGuideReadinessList;
